<template>
  <div class="h-100">
    <div class="compound-extracts-table__container h-100">
      <CompoundExtractsToolbar
        @update-selected-extract-filter="updateCreatorFilter"
        @update-extract-name-filter="updateExtractNameFilter"
      />

      <div class="flex-grow-1">
        <AgGridVue
          class="h-100 w-100"
          :does-external-filter-pass="doesExternalFilterPass"
          :loading="isGridLoading || isProcessing"
          :row-data="rowData"
          :column-defs="columnDefs"
          :grid-options="gridOptions"
          :overlay-no-rows-template="overlayNoRowsTemplate"
          @grid-ready="onGridReady"
        />
      </div>
      <v-row class="actions-row pb-4 ma-0 justify-end">
        <div class="d-flex align-center action-btn-container">
          <v-menu id="myMenu" width="500">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                id="btnGenerateExtracts"
                data-id-e2e="btnGenerateExtracts"
                max-height="28"
                action
                min-width="64"
                variant="flat"
                :disabled="!allowedItems.write.length || isProcessing"
              >
                {{ $t('actions.newExtract') }}
              </v-btn>
            </template>

            <v-list>
              <span
                v-for="({ name, reportType, subType, tkey: translation }, i) in allowedItems.write"
                :id="`btnFetchCompound${reportType}`"
                :key="i"
                :to="{ name }"
                class="extract-link selectable"
                @click="() => onRouteClick(name, { type: reportType, subType })"
              >
                <v-list-item>
                  <v-list-item-title>{{ $t(translation) }}</v-list-item-title>
                </v-list-item>
              </span>
            </v-list>
          </v-menu>
        </div>
      </v-row>
    </div>
  </div>
</template>

<script>
import { get, map, isEqual, reduce, isEmpty, each, includes, debounce, some } from 'lodash';
import { mapState, mapActions } from 'pinia';
import to from 'await-to-js';
import userPermissions from '@enums/user-permissions';
import compoundExtractFilters from '@enums/compound-extract-filters';
import extractExportTypes from '@enums/extract-export-types';
import jobapi from '@enums/jobapi';
import ftpUploadTypes from '@enums/ftp-upload-types';
import agGridUtils from '@/utils/ag-grid-utils';
import AgGridButtonRendererDownload from '@/components/AgGridButtonRendererDownload.vue';
import AgGridButtonRendererDuplicate from '@/components/AgGridButtonRendererDuplicate.vue';
import AgGridButtonRendererUpload from '@/components/AgGridButtonRendererUpload.vue';
import AgGridStatusRendererOutbound from '@/components/AgGridStatusRendererOutbound.vue';
import AgGridButtonRendererDelete from '@/components/AgGridButtonRendererDelete.vue';

import { useBundlesStore } from '@/stores/bundles';
import { useContextStore } from '@/stores/context';
import { useSnackbarStore } from '@/stores/snackbar';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useCompoundExtractStore } from '@/stores/compound-extract';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import { useCompoundExtract } from '@/composables/use-compound-extract';
import searchUtils from '@/utils/search-utils';

const { canCreateAocExtracts, canViewAocExtracts, canCreateHasuExtracts, canViewHasuExtracts } =
  userPermissions;

const { jobFinishedStatuses } = jobapi;

const fieldEnum = {
  bundle: 'bundle',
  workpackage: 'workpackage',
  created: 'extractDatetime',
  creator: 'createdBy.fullName',
  type: 'type',
  duplicate: 'duplicate',
  download: 'download',
  upload: 'upload',
  planningHub: 'planningHub',
  spaceTest: 'spaceTest',
  delete: 'delete',
  filenameOnDownload: 'filenameOnDownload',
};

const seperator = ' | ';

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    AgGridButtonRendererDuplicate,
    AgGridButtonRendererUpload,
    AgGridButtonRendererDownload,
    AgGridStatusRendererOutbound,
    AgGridButtonRendererDelete,
  },

  setup() {
    const contextStore = useContextStore();
    const bundlesStore = useBundlesStore();
    const snackbarStore = useSnackbarStore();

    const { tkey, t } = useInternationalization('compoundExtractsPage.table');
    const { availableExtractsExports, ftpSpaceTestEnabled, betaIsExtractDeleteEnabled } =
      useFeature();
    const { formatExtractType, isSpaceTest } = useCompoundExtract();

    return {
      bundlesStore,
      tkey,
      t,
      bundlesEnabled: contextStore.bundlesEnabled,
      availableExtractsExports,
      ftpSpaceTestEnabled,
      betaIsExtractDeleteEnabled,
      snackbarStore,
      formatExtractType,
      isSpaceTest,
    };
  },

  data() {
    return {
      isGridLoading: false, // controls grid overlay
      items: {
        hasu: {
          name: 'hasu-extract',
          tkey: 'compoundExtractsPage.hasu',
          permissions: {
            read: canViewHasuExtracts,
            write: canCreateHasuExtracts,
          },
        },
        aoc: {
          name: 'aoc-extract',
          tkey: 'compoundExtractsPage.aoc',
          permissions: {
            read: canViewAocExtracts,
            write: canCreateAocExtracts,
          },
        },
        spaceTest: {
          name: 'aoc-extract',
          tkey: 'compoundExtractsPage.spaceTest',
          permissions: {
            read: canViewAocExtracts,
            write: canCreateAocExtracts,
          },
          subType: extractExportTypes.extractExportSubTypes.spaceTest,
        },
      },
      gridOptions: {
        columnMenu: 'legacy',
        rowHeight: 50,
        headerHeight: 50,
        suppressContextMenu: true,
        tooltipShowDelay: 0,
        defaultColDef: {
          filter: true,
          sortable: true,
          resizable: false,
          minWidth: 80,
          editable: false,
          suppressMovable: true,
          flex: 1,
          menuTabs: ['filterMenuTab'],
          cellClass: 'no-border',
        },
        onFilterChanged: this.onFilterChanged,
        isExternalFilterPresent: () => true,
      },
      gridApi: null,
      rowData: null,
      overlayNoRowsTemplate: null,
      searchString: '',
    };
  },
  computed: {
    ...mapState(useCompoundExtractStore, [
      'extracts',
      'isProcessing',
      'selectedExtractFilter',
      'extractSeriesIdBySettingId',
      'extractSeriesById',
    ]),
    ...mapState(useContextStore, [
      'getClientConfig',
      'getDateFormats',
      'getFullName',
      'hasPermission',
    ]),
    ...mapState(useWorkpackagesStore, ['workpackagesById']),

    columnDefs() {
      return [
        {
          field: fieldEnum.filenameOnDownload,
          colId: fieldEnum.filenameOnDownload,
          headerName: this.tkey(`columns.${fieldEnum.filenameOnDownload}`),
          filter: 'agTextColumnFilter',
          filterParams: agGridUtils.filters.standardStringFilter,
          tooltipValueGetter: this.getTooltipValue,
          maxWidth: 300,
        },
        {
          field: fieldEnum.creator,
          colId: fieldEnum.creator,
          headerName: this.tkey('columns.creator'),
          maxWidth: 180,
          tooltipValueGetter: this.getTooltipValue,
        },
        {
          field: fieldEnum.created,
          colId: fieldEnum.created,
          headerName: this.tkey('columns.created'),
          sort: 'desc',
          valueFormatter: params =>
            agGridUtils.formatters.datesFormatter(params, this.getDateFormats.mediumWithTime),
          filterParams: {
            valueFormatter: params =>
              agGridUtils.formatters.datesFormatter(params, this.getDateFormats.mediumWithTime),
          },
          maxWidth: 110,
        },
        {
          field: fieldEnum.bundle,
          colId: fieldEnum.bundle,
          headerName: this.tkey(`columns.${fieldEnum.bundle}`),
          hide: !this.bundlesEnabled,
          filter: 'agTextColumnFilter',
          filterParams: agGridUtils.filters.standardStringFilter,
          maxWidth: 250,
          tooltipValueGetter: this.getTooltipValue,
        },
        {
          field: fieldEnum.workpackage,
          colId: fieldEnum.workpackage,
          headerName: this.tkey(`columns.${fieldEnum.workpackage}`),
          filter: 'agTextColumnFilter',
          filterParams: agGridUtils.filters.standardStringFilter,
          maxWidth: 250,
          tooltipValueGetter: this.getTooltipValue,
        },
        {
          field: fieldEnum.type,
          colId: fieldEnum.type,
          headerName: this.tkey(`columns.${fieldEnum.type}`),
          maxWidth: 85,
          // Show extract type in uppercase
          valueFormatter: params =>
            this.formatExtractType({
              t: this.t,
              type: params.value,
              subType: params.data.subType,
            }),
          filterParams: {
            valueFormatter: params =>
              this.formatExtractType({
                t: this.t,
                type: params.value,
                subType: params.data.subType,
              }),
          },
        },
        {
          field: fieldEnum.planningHub,
          colId: fieldEnum.planningHub,
          headerName: this.t('compoundExtractsPage.planningHub'),
          width: 145,
          sortable: false,
          filter: false,
          menuTabs: [],
          cellRenderer: 'AgGridStatusRendererOutbound',
          cellRendererParams: params => {
            return {
              mostRecentOutbound: params.data.mostRecentOutbound,
              outboundHistory: params.data.outboundHistory,
              outboundType: ftpUploadTypes.planningHub,
              extractId: params.data._id,
              extractTypes: this.extractTypes,
              refresh: this.init,
            };
          },
        },
        {
          field: fieldEnum.spaceTest,
          colId: fieldEnum.spaceTest,
          headerName: this.t('compoundExtractsPage.spaceTest'),
          width: 145,
          sortable: false,
          filter: false,
          menuTabs: [],
          hide: !this.ftpSpaceTestEnabled,
          cellRenderer: 'AgGridStatusRendererOutbound',
          cellRendererParams: params => {
            return {
              mostRecentOutbound: params.data.mostRecentOutbound,
              outboundHistory: params.data.outboundHistory,
              outboundType: ftpUploadTypes.spaceTest,
              extractId: params.data._id,
              extractTypes: this.extractTypes,
              refresh: this.init,
            };
          },
        },
        {
          field: fieldEnum.duplicate,
          colId: fieldEnum.duplicate,
          headerName: this.tkey(`columns.${fieldEnum.duplicate}`),
          minWidth: 60,
          maxWidth: 60,
          sortable: false,
          filter: false,
          menuTabs: [],
          cellRenderer: 'AgGridButtonRendererDuplicate',
          cellRendererParams: params => ({
            extractId: params.data._id,
            type: params.data.type,
            extractSettingsId: params.data.extractSettingsIds[0], // TODO AOV3-2337 - This should take the most up to date version, i.e. highest version.
            extractSeries:
              this.extractSeriesById[
                this.extractSeriesIdBySettingId[params.data.extractSettingsIds[0]] // TODO AOV3-2337 - This should take the most up to date settings version, i.e. highest version.
              ],
          }),
        },
        {
          field: fieldEnum.delete,
          colId: fieldEnum.delete,
          headerName: '',
          hide: !this.betaIsExtractDeleteEnabled,
          maxWidth: 60,
          sortable: false,
          filter: false,
          menuTabs: [],
          cellRenderer: 'AgGridButtonRendererDelete',
          cellRendererParams: params => {
            return {
              extractId: params.data._id,
              filenameOnDownload: params.data.filenameOnDownload,
              refresh: this.init,
            };
          },
        },
        {
          field: fieldEnum.download,
          colId: fieldEnum.download,
          headerName: '',
          maxWidth: 60,
          minWidth: 60,
          sortable: false,
          filter: false,
          menuTabs: [],
          cellRenderer: 'AgGridButtonRendererDownload',
          cellRendererParams: params => ({
            extractId: params.data._id,
            extractTypes: this.extractTypes,
          }),
        },
        {
          field: fieldEnum.upload,
          colId: fieldEnum.upload,
          headerName: '',
          maxWidth: 60,
          sortable: false,
          filter: false,
          menuTabs: [],
          cellRenderer: 'AgGridButtonRendererUpload',
          cellRendererParams: params => {
            return {
              mostRecentOutbound: params.data.mostRecentOutbound,
              spaceTestOnly: this.isSpaceTest(params.data.subType),
              extractId: params.data._id,
              extractTypes: this.extractTypes,
              refresh: this.init,
            };
          },
        },
      ];
    },

    allowedItems() {
      const options = [...this.availableExtractsExports];

      if (this.ftpSpaceTestEnabled) {
        options.push('spaceTest');
      }

      return options.reduce(
        (enabledExtracts, exportType) => {
          const item = this.items[exportType];
          if (item) {
            item.reportType = exportType;

            // spaceTest is a subtype of aoc extract
            if (exportType === 'spaceTest') {
              item.reportType = 'aoc';
            }

            each(item.permissions, (permission, type) => {
              if (this.hasPermission(permission)) {
                enabledExtracts[type].push(item);
              }
            });
          }

          return enabledExtracts;
        },
        { read: [], write: [] }
      );
    },

    extractTypes() {
      // Only load the extracts the user has permission to view
      return map(this.allowedItems.read, 'reportType');
    },

    // TODO add pagination to the table and figure out how to handle the polling!
    shouldPollExtracts() {
      // checks if there are unfinished jobs that need to be refreshed
      return some(
        this.extracts,
        e => !includes(jobFinishedStatuses, get(e, 'jobs.compoundExtract.status'))
      );
    },
  },

  watch: {
    extracts() {
      if (this.shouldPollExtracts) {
        this.debouncedFetchExtracts();
      }
    },
  },

  created() {
    this.debouncedFetchExtracts = debounce(this.fetchExtractInstances, 30000);
    this.init();
  },
  mounted() {
    this.overlayNoRowsTemplate = `<span>${this.tkey('noExtracts')}</span>`;
  },

  methods: {
    ...mapActions(useCompoundExtractStore, [
      'fetchExtractSeries',
      'fetchExtractInstances',
      'setSelectedExtractFilter',
      'generateExtract',
      'setIsProcessing',
    ]),
    ...mapActions(useWorkpackagesStore, ['fetchWorkpackages']),

    async init() {
      const pick = ['_id', 'name', 'creationDate'];

      const promises = [
        this.fetchWorkpackages({ shouldGetTemplates: false, pick }),
        this.fetchExtractSeries({ extractTypes: this.extractTypes }),
        this.fetchExtractInstances({ extractTypes: this.extractTypes }),
      ];
      if (this.bundlesEnabled) {
        promises.push(this.bundlesStore.fetchBundles({ pick }));
      }
      await Promise.all(promises);

      this.loadExtractData();
    },

    async loadExtractData() {
      this.rowData = reduce(
        this.extracts,
        (rows, e) => {
          const extractSeries = get(
            this.extractSeriesById,
            this.extractSeriesIdBySettingId[e.extractSettingsId]
          );
          if (extractSeries) {
            rows.push({
              ...extractSeries,
              _id: e._id,
              blobId: e.blobId,
              filenameOnDownload: e.filenameOnDownload,
              jobs: e.jobs,
              extractDatetime: e.extractDatetime,
              bundle: extractSeries.bundleIds
                .reduce((bundles, id) => {
                  const bundle = this.bundlesStore.bundlesById[id];
                  if (bundle) bundles.push(bundle.name);
                  return bundles;
                }, [])
                .join(seperator),
              workpackage: extractSeries.workpackageIds
                .reduce((workpackages, id) => {
                  const wp = this.workpackagesById[id];
                  if (wp) workpackages.push(wp.name);
                  return workpackages;
                }, [])
                .join(seperator),
            });
          }
          return rows;
        },
        []
      );
    },

    onGridReady(params) {
      this.gridApi = params.api;

      // Applies correct filter to the creator column on load
      setTimeout(() => {
        this.updateCreatorFilter(this.selectedExtractFilter);
      }, 150);
    },

    onFilterChanged(params) {
      if (!params.api) return;
      const filterComponent = params.api.getColumnFilterModel(fieldEnum.creator);
      const allCreatorValues = get(filterComponent, 'valueModel.availableValues', new Set());
      const allExtractsCreatedByUser =
        allCreatorValues.size === 1 && allCreatorValues.has(this.getFullName);
      const userSelected = get(filterComponent, 'valueModel.selectedValues', new Set()).has(
        this.getFullName
      );
      const userFilterSelected = this.selectedExtractFilter === this.getFullName;
      const currentModelValues = get(filterComponent, 'values', []);
      const noFilterValues = currentModelValues && isEmpty(currentModelValues);

      /*
        Updates the toolbar toggle when selections are made through ag-grid filter.
        Ensures that the current user filter toggle is selected when:
          1. All extracts in the table have been created by the user and the current user toggle is selected
          2. Extracts by multiple authors exist but only filtering by the current user
          3. The current user toggle is selected but there are no extracts created by the user
        */
      const selectedFilter =
        (allExtractsCreatedByUser && userSelected && userFilterSelected) ||
        isEqual(currentModelValues, [this.getFullName]) ||
        (noFilterValues && userFilterSelected)
          ? this.getFullName
          : compoundExtractFilters.all;
      this.setSelectedExtractFilter(selectedFilter);

      // If there are no rows in the table, show no rows overlay
      if (!this.gridApi.getDisplayedRowCount()) {
        this.gridApi.showNoRowsOverlay();
      } else {
        this.isGridLoading = false;
      }
    },

    updateCreatorFilter(filter) {
      if (!this.gridApi) return;
      // Auto set the table to filter by creator. To show extracts from all creators, value must be null.
      const updatedModelValues = filter === compoundExtractFilters.all ? null : [filter];
      const agGridFilter = {
        type: 'set',
        values: updatedModelValues,
      };
      this.gridApi.setColumnFilterModel(fieldEnum.creator, agGridFilter);
      this.setSelectedExtractFilter(filter);
      this.gridApi.onFilterChanged();
    },

    getTooltipValue(params) {
      return get(params.data, params.column.colId, '');
    },

    async onRouteClick(routeName, extractParams) {
      this.setIsProcessing(true);
      const [err, newExtract] = await to(this.generateExtract(extractParams));
      this.setIsProcessing(false);
      if (err) {
        this.snackbarStore.showWarning();
        return;
      }

      this.$router.push({
        name: routeName,
        params: { extractSeriesId: newExtract.series._id },
        query: this.$route.query,
      });
    },

    doesExternalFilterPass(node) {
      const filenameOnDownload = get(node.data, 'filenameOnDownload');
      const creator = get(node.data, 'createdBy.fullName');

      if (isEmpty(this.searchString)) return true;

      const isExtractFound = searchUtils.searchRowsByFields(this.searchString, [
        filenameOnDownload,
        creator,
      ]);

      return isExtractFound;
    },

    updateExtractNameFilter(value) {
      this.searchString = value;
      this.gridApi.onFilterChanged();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.compound-extracts-table {
  &__container {
    display: flex;
    flex-direction: column;
  }

  &__toolbar {
    border-bottom: 1px solid $assortment-panel-header-border;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__toggle {
    display: flex;
  }
}

:deep() {
  .generate-extracts {
    &__button {
      height: 32px !important;
      padding: 0 12px !important;

      span {
        font-size: 1.6rem;
      }
    }

    &__icon {
      margin-left: 6px;
    }
  }
}
</style>
